import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils/cn';
import { motion } from 'motion/react';

interface WorkflowNode {
  id: string;
  label: string;
  color: string;
  description?: string;
}

interface WorkflowEdge {
  from: string;
  to: string;
  label: string;
}

const NODES: WorkflowNode[] = [
  {
    id: 'draft',
    label: 'BORRADOR',
    color: 'bg-slate-500',
    description: 'El vendedor crea y edita la cotizacion',
  },
  {
    id: 'pending',
    label: 'PENDIENTE',
    color: 'bg-yellow-500',
    description: 'Enviada al aprobador para revision',
  },
  {
    id: 'approved',
    label: 'APROBADA',
    color: 'bg-green-500',
    description: 'El aprobador acepto la cotizacion',
  },
  {
    id: 'rejected',
    label: 'RECHAZADA',
    color: 'bg-red-500',
    description: 'El aprobador rechazo la cotizacion',
  },
  {
    id: 'adjustment',
    label: 'AJUSTE',
    color: 'bg-orange-500',
    description: 'El aprobador pide cambios al vendedor',
  },
];

const EDGES: WorkflowEdge[] = [
  { from: 'draft', to: 'pending', label: 'Enviar' },
  { from: 'pending', to: 'approved', label: 'Aprobar' },
  { from: 'pending', to: 'rejected', label: 'Rechazar' },
  { from: 'pending', to: 'adjustment', label: 'Pedir Ajuste' },
  { from: 'adjustment', to: 'pending', label: 'Re-enviar' },
];

// Auto-advance animation cycle
const ANIMATION_SEQUENCE = ['draft', 'pending', 'approved'];

export function WorkflowDiagram() {
  const [activeNode, setActiveNode] = useState('draft');
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    if (!isAnimating) return;
    let step = 0;
    const interval = setInterval(() => {
      step = (step + 1) % ANIMATION_SEQUENCE.length;
      setActiveNode(ANIMATION_SEQUENCE[step]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAnimating]);

  return (
    <div className="space-y-4">
      {/* Main flow */}
      <div className="overflow-x-auto">
        <div className="flex items-center gap-3 min-w-[600px] py-4">
          {/* BORRADOR → PENDIENTE → APROBADA */}
          {['draft', 'pending', 'approved'].map((nodeId, i) => {
            const node = NODES.find((n) => n.id === nodeId)!;
            const isActive = activeNode === nodeId;
            return (
              <div key={nodeId} className="flex items-center gap-3">
                <motion.button
                  onClick={() => {
                    setIsAnimating(false);
                    setActiveNode(nodeId);
                  }}
                  animate={isActive ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 1 }}
                  className={cn(
                    'px-5 py-2.5 rounded-lg text-white text-sm font-semibold text-center shadow-sm min-w-[120px] transition-all cursor-pointer',
                    node.color,
                    isActive && 'ring-4 ring-offset-2',
                    isActive && nodeId === 'draft' && 'ring-slate-300',
                    isActive && nodeId === 'pending' && 'ring-yellow-300',
                    isActive && nodeId === 'approved' && 'ring-green-300'
                  )}
                >
                  {node.label}
                </motion.button>
                {i < 2 && (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-[10px] text-muted-foreground">
                      {
                        EDGES.find(
                          (e) =>
                            e.from === nodeId && e.to === ['draft', 'pending', 'approved'][i + 1]
                        )?.label
                      }
                    </span>
                    <div className="w-10 h-0.5 bg-muted-foreground relative">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[6px] border-l-muted-foreground" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Branch: PENDIENTE → RECHAZADA / AJUSTE */}
        <div className="flex items-center gap-6 min-w-[600px] pl-[180px]">
          {['rejected', 'adjustment'].map((nodeId) => {
            const node = NODES.find((n) => n.id === nodeId)!;
            const isActive = activeNode === nodeId;
            const edge = EDGES.find((e) => e.from === 'pending' && e.to === nodeId)!;
            return (
              <div key={nodeId} className="flex items-center gap-2">
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-[10px] text-muted-foreground">{edge.label}</span>
                  <div className="w-8 h-0.5 bg-muted-foreground relative">
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[6px] border-l-muted-foreground" />
                  </div>
                </div>
                <motion.button
                  onClick={() => {
                    setIsAnimating(false);
                    setActiveNode(nodeId);
                  }}
                  animate={isActive ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                  transition={{ duration: 0.5, repeat: isActive ? Infinity : 0, repeatDelay: 1 }}
                  className={cn(
                    'px-5 py-2.5 rounded-lg text-white text-sm font-semibold text-center shadow-sm min-w-[120px] cursor-pointer',
                    node.color,
                    isActive && 'ring-4 ring-offset-2',
                    isActive && nodeId === 'rejected' && 'ring-red-300',
                    isActive && nodeId === 'adjustment' && 'ring-orange-300'
                  )}
                >
                  {node.label}
                </motion.button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Description of active node */}
      <motion.div
        key={activeNode}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-muted/50 rounded-lg p-3"
      >
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">
            {NODES.find((n) => n.id === activeNode)?.label}:
          </strong>{' '}
          {NODES.find((n) => n.id === activeNode)?.description}
        </p>
      </motion.div>
    </div>
  );
}
